"use client";

import { RefObject, useCallback, useEffect, useState } from "react";

export type UseFullscreenResult = {
  isFullscreen: boolean;
  supported: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
  error: Error | null;
};

/**
 * Minimal fullscreen hook using the native Fullscreen API.
 * Imperative entry keeps the user gesture context inside the click handler.
 */
const useFullscreen = (
  ref: RefObject<HTMLElement | null>,
): UseFullscreenResult => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const supported =
    typeof document !== "undefined" &&
    !!document.documentElement.requestFullscreen;

  // Sync state when ESC pressed or fullscreen changes.
  useEffect(() => {
    if (typeof document === "undefined") return;

    const handler = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (!active) {
        // Clear stale errors on normal close
        setError(null);
      }
    };

    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const enter = useCallback(async () => {
    setError(null);
    try {
      if (!ref.current) throw new Error("Target element not available.");
      if (!supported) throw new Error("Fullscreen API not supported.");
      await ref.current.requestFullscreen();
      // state will update via event; optimistic set avoids flicker
      setIsFullscreen(true);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [supported, ref]);

  const exit = useCallback(async () => {
    setError(null);
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setIsFullscreen(false);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  const toggle = useCallback(async () => {
    if (isFullscreen) {
      await exit();
    } else {
      await enter();
    }
  }, [isFullscreen, enter, exit]);

  return { isFullscreen, supported, enter, exit, toggle, error };
};

export default useFullscreen;
